/**
 * Serviço de autenticação segura com hash de senha
 * Utiliza Web Crypto API para hash seguro das senhas
 */

import { env } from '../config/environment';
import { normalizeTimestamp } from '../utils/timestampNormalizer';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';
import { consumeLogin, resetLoginLimit } from '../utils/rateLimiter';

// Interface para dados do usuário
interface UserCredentials {
  username: string;
  passwordHash: string;
  salt: string;
  id: string;
  createdAt: string;
  lastLogin?: string;
}

// Interface para resultado de autenticação
interface AuthResult {
  success: boolean;
  user?: {
    id: string;
    username: string;
    lastLogin: string;
  };
  error?: string;
}

// Classe para gerenciar autenticação segura
class AuthService {
  private readonly STORAGE_KEY = 'psicosys_auth_data';
  private readonly ITERATIONS = 100000; // Número de iterações para PBKDF2
  
  /**
   * Gera um salt aleatório
   */
  private generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(16));
  }
  
  /**
   * Converte ArrayBuffer para string hexadecimal
   */
  private bufferToHex(buffer: ArrayBuffer): string {
    return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }
  
  /**
   * Converte string hexadecimal para Uint8Array
   */
  private hexToBuffer(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  }
  
  /**
   * Gera hash da senha usando PBKDF2
   */
  private async hashPassword(password: string, salt: Uint8Array): Promise<string> {
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);
    
    // Importar a senha como chave
    const key = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );
    
    // Derivar bits usando PBKDF2
    const hashBuffer = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: this.ITERATIONS,
        hash: 'SHA-256'
      },
      key,
      256 // 32 bytes
    );
    
    return this.bufferToHex(hashBuffer);
  }
  
  /**
   * Verifica se a senha está correta
   */
  private async verifyPassword(password: string, hash: string, salt: string): Promise<boolean> {
    try {
      const saltBuffer = this.hexToBuffer(salt);
      const computedHash = await this.hashPassword(password, saltBuffer);
      return computedHash === hash;
    } catch (error) {
      console.error('Erro ao verificar senha:', error);
      return false;
    }
  }
  
  /**
   * Cria um novo usuário com senha hasheada
   */
  async createUser(username: string, password: string): Promise<AuthResult> {
    try {
      // Validar entrada
      if (!username || !password) {
        return { success: false, error: 'Username e senha são obrigatórios' };
      }
      
      if (password.length < 6) {
        return { success: false, error: 'Senha deve ter pelo menos 6 caracteres' };
      }
      
      // Verificar se usuário já existe
      const existingUsers = this.getStoredUsers();
      if (existingUsers.some(u => u.username === username)) {
        return { success: false, error: 'Usuário já existe' };
      }
      
      // Gerar salt e hash da senha
      const salt = this.generateSalt();
      const passwordHash = await this.hashPassword(password, salt);
      
      // Criar dados do usuário
      const userData: UserCredentials = {
        username,
        passwordHash,
        salt: this.bufferToHex(salt),
        id: crypto.randomUUID(),
        createdAt: normalizeTimestamp(new Date())
      };
      
      // Salvar usuário
      existingUsers.push(userData);
      this.saveUsers(existingUsers);
      
      return {
        success: true,
        user: {
          id: userData.id,
          username: userData.username,
          lastLogin: normalizeTimestamp(new Date())
        }
      };
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      return { success: false, error: 'Erro interno do servidor' };
    }
  }
  
  /**
   * Autentica um usuário
   */
  async authenticateUser(username: string, password: string): Promise<AuthResult> {
    try {
      // Validar entrada
      if (!username || !password) {
        return { success: false, error: 'Username e senha são obrigatórios' };
      }

      // Rate limiting - previne ataques de força bruta
      const rateLimitResult = consumeLogin(username);
      if (!rateLimitResult.allowed) {
        return {
          success: false,
          error: `Muitas tentativas de login. Tente novamente em 15 minutos. (${rateLimitResult.remaining} tentativas restantes)`
        };
      }

      // Buscar usuário
      const users = this.getStoredUsers();
      const user = users.find(u => u.username === username);
      
      if (!user) {
        // No hardcoded credentials - authentication must be done via Supabase
        return { success: false, error: 'Usuário não encontrado' };
      }
      
      // Verificar senha
      const isValidPassword = await this.verifyPassword(password, user.passwordHash, user.salt);
      
      if (!isValidPassword) {
        return { success: false, error: 'Senha incorreta' };
      }
      
      // Atualizar último login
      user.lastLogin = normalizeTimestamp(new Date());
      this.saveUsers(users);

      // Reset rate limit após login bem-sucedido
      resetLoginLimit(username);

      return {
        success: true,
        user: {
          id: user.id,
          username: user.username,
          lastLogin: user.lastLogin
        }
      };
    } catch (error) {
      console.error('Erro na autenticação:', error);
      return { success: false, error: 'Erro interno do servidor' };
    }
  }
  
  /**
   * Obtém usuários armazenados
   */
  private getStoredUsers(): UserCredentials[] {
    try {
      const stored = safeGetItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const parsed = JSON.parse(stored);
      // Se os dados estão na estrutura { users: [...] }, acessar a propriedade users
      if (parsed && parsed.users && Array.isArray(parsed.users)) {
        return parsed.users;
      }
      // Se os dados são diretamente um array
      if (Array.isArray(parsed)) {
        return parsed;
      }
      
      return [];
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      return [];
    }
  }
  
  /**
   * Salva usuários no localStorage
   */
  private saveUsers(users: UserCredentials[]): void {
    try {
      // Manter a estrutura { users: [...] } para compatibilidade
      const authData = { users };
      safeSetItem(this.STORAGE_KEY, JSON.stringify(authData));
    } catch (error) {
      console.error('Erro ao salvar usuários:', error);
    }
  }
  
  /**
   * Altera senha do usuário
   */
  async changePassword(username: string, currentPassword: string, newPassword: string): Promise<AuthResult> {
    try {
      // Validar entrada
      if (!username || !currentPassword || !newPassword) {
        return { success: false, error: 'Todos os campos são obrigatórios' };
      }
      
      if (newPassword.length < 6) {
        return { success: false, error: 'Nova senha deve ter pelo menos 6 caracteres' };
      }
      
      // Autenticar com senha atual
      const authResult = await this.authenticateUser(username, currentPassword);
      if (!authResult.success) {
        return { success: false, error: 'Senha atual incorreta' };
      }
      
      // Buscar usuário
      const users = this.getStoredUsers();
      const userIndex = users.findIndex(u => u.username === username);
      
      if (userIndex === -1) {
        return { success: false, error: 'Usuário não encontrado' };
      }
      
      // Gerar novo hash
      const salt = this.generateSalt();
      const passwordHash = await this.hashPassword(newPassword, salt);
      
      // Atualizar usuário
      users[userIndex].passwordHash = passwordHash;
      users[userIndex].salt = this.bufferToHex(salt);
      
      this.saveUsers(users);
      
      return { success: true };
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      return { success: false, error: 'Erro interno do servidor' };
    }
  }
  
  /**
   * Remove dados de autenticação (para desenvolvimento)
   */
  clearAuthData(): void {
    if (env.app.environment === 'development') {
      safeRemoveItem(this.STORAGE_KEY);
      console.log('Dados de autenticação removidos');
    }
  }
  
  /**
   * Solicita redefinição de senha via Supabase
   */
  async resetPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!email) {
        return { success: false, error: 'Email é obrigatório' };
      }

      // Try Supabase password reset
      try {
        const { createClient } = await import('@supabase/supabase-js');
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

        if (supabaseUrl && supabaseKey) {
          const supabase = createClient(supabaseUrl, supabaseKey);
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/login?reset=true`,
          });

          if (error) {
            console.error('Supabase reset error:', error);
          }
        }
      } catch (e) {
        console.error('Supabase not available for password reset:', e);
      }

      // Always return success to prevent email enumeration
      return { success: true };
    } catch (error) {
      console.error('Erro ao solicitar redefinição de senha:', error);
      return { success: false, error: 'Erro ao processar solicitação' };
    }
  }

  /**
   * Lista usuários (apenas para desenvolvimento)
   */
  listUsers(): Array<{ username: string; id: string; createdAt: string; lastLogin?: string }> {
    if (env.app.environment === 'production') {
      return [];
    }
    
    return this.getStoredUsers().map(user => ({
      username: user.username,
      id: user.id,
      createdAt: user.createdAt,
      lastLogin: user.lastLogin
    }));
  }
}

// Exportar instância singleton
export const authService = new AuthService();
export type { AuthResult };